import React from 'react';
import { Container, Box, Button, Typography, ToggleButtonGroup, ToggleButton, FormControlLabel, Switch, Tooltip, FormControl, Select, MenuItem, InputLabel } from '@mui/material';
import { useTranslation } from "react-i18next";
import DescriptionIcon from '@mui/icons-material/Description';
import ScannerIcon from '@mui/icons-material/Scanner';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { pdfjs } from 'react-pdf';

function Header({ onFileSelect, compact = false, useOcr, setUseOcr, useDeepScan, setUseDeepScan, spatialCloseness, setSpatialCloseness, useSpatialLocality, setUseSpatialLocality, provider, setProvider, model, setModel, availableModels, configuredProviders }) {
    const { t } = useTranslation();
    
    async function handleFileChange(event) {
        const selectedFile = event.target.files[0];
        if (selectedFile) {
            try {
                const arrayBuffer = await selectedFile.arrayBuffer();
                const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
                const pageCount = pdf.numPages;

                if (pageCount > 10) {
                    alert(t("pdf_too_many_pages", "Please upload a PDF with maximum {{maxPages}} pages. Your document has {{pageCount}} pages.", { maxPages: 10, pageCount }));
                    event.target.value = '';
                    return;
                }

                onFileSelect(selectedFile);
            } catch (error) {
                console.error('Error reading PDF:', error);
                alert(t("pdf_read_error", "Error reading PDF file. Please try again."));
                event.target.value = '';
            }
        }
    }

    const handleOcrToggle = (event, newValue) => {
        if (newValue !== null && setUseOcr) {
            setUseOcr(newValue);
        }
    };

    const zFixProps = {
        MenuProps: {
            PaperProps: {sx: {zIndex: 3000}},
            PopoverClasses: {paper: 'z-fix'},
        }
    };

    return (
        <Container maxWidth="lg" sx={{ py: compact ? 0 : 2, pl: compact ? 2 : '90px !important' }}>
            <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                justifyContent="flex-start"
                gap={1}
                sx={{
                    mb: compact ? 1 : 1,
                    ml: compact ? 0 : 10,
                    ...(compact ? {} : { maxWidth: '900px' }),
                }}
            >
                {configuredProviders && configuredProviders.length > 0 && (
                    <Box display="flex" flexDirection="column" gap={0.5} sx={{ mr: 2, flexShrink: 0 }}>
                        <FormControl size="small" sx={{ minWidth: 140, bgcolor: 'background.paper', borderRadius: 1 }}>
                            <Select
                                id="provider-select"
                                value={provider || ''}
                                onChange={(e) => {
                                    const newProvider = e.target.value;
                                    if (setProvider) {
                                        setProvider(newProvider);
                                        // Don't auto-select model - user must explicitly choose
                                    }
                                }}
                                displayEmpty
                                sx={{ height: 32, fontSize: '0.875rem' }}
                                {...zFixProps}
                            >
                                {configuredProviders.map(p => (
                                    <MenuItem key={p} value={p}>{p.toUpperCase()}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 140, bgcolor: 'background.paper', borderRadius: 1 }}>
                            <Select
                                id="model-select"
                                value={model || ''}
                                onChange={(e) => setModel && setModel(e.target.value)}
                                displayEmpty
                                disabled={!provider}
                                sx={{ height: 32, fontSize: '0.875rem' }}
                                {...zFixProps}
                            >
                                <MenuItem value="" disabled>
                                    <em>{t("select_model", "Select Model")}</em>
                                </MenuItem>
                                {(availableModels[provider] || []).map((m) => (
                                    <MenuItem key={m} value={m}>{m}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                )}
                <Box
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="center"
                    gap={1}
                    sx={{ flex: 1 }}
                >
                    <Typography
                        variant={compact ? "body2" : "body1"}
                        align="center"
                    >
                        {compact
                            ? t("upload_instruction_compact", "Upload a document")
                            : t("upload_instruction", "Please upload your document to start configuring your tool")}
                    </Typography>
                <Button
                    variant="contained"
                    component="label"
                    sx={{
                        alignSelf: 'center',
                        display: 'flex',
                        justifyContent: 'center',
                        textAlign: 'center',
                        px: 2, // horizontal padding
                        py: compact? 0 : 0.5, // vertical padding
                    }}
                >
                    {t("upload_pdf")}
                    <input
                        type="file"
                        accept="application/pdf"
                        hidden
                        onChange={handleFileChange}
                    />
                </Button>
                {setUseOcr !== undefined && (
                    <ToggleButtonGroup
                        value={useOcr}
                        exclusive
                        onChange={handleOcrToggle}
                        size="small"
                        sx={{ ml: 1 }}
                    >
                        <ToggleButton value={false} aria-label="digital document" sx={{ px: 1, py: 0.5 }}>
                            <DescriptionIcon sx={{ mr: 0.3 }} fontSize="small" />
                            <Typography variant="caption">{t("digital_document", "Digital")}</Typography>
                        </ToggleButton>
                        <ToggleButton value={true} aria-label="scanned document" sx={{ px: 1, py: 0.5 }}>
                            <ScannerIcon sx={{ mr: 0.3 }} fontSize="small" />
                            <Typography variant="caption">{t("scanned_document", "Scanned")}</Typography>
                        </ToggleButton>
                    </ToggleButtonGroup>
                )}
                {setUseDeepScan !== undefined && useOcr && (
                    <FormControlLabel
                        control={
                            <Switch
                                checked={useDeepScan}
                                onChange={(e) => setUseDeepScan(e.target.checked)}
                                color="primary"
                                size="small"
                            />
                        }
                        label={
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.3}}>
                                <Typography variant="caption">
                                    {t('deep_scan.label', 'Deep Scan')}
                                </Typography>
                                <Tooltip
                                    title={t('deep_scan.tooltip', 'Enhanced OCR for degraded or handwritten documents from the 1970s. Uses advanced preprocessing and multi-pass recognition. Slower but more accurate.')}
                                    arrow
                                >
                                    <InfoOutlinedIcon sx={{fontSize: 14, color: 'text.secondary'}} />
                                </Tooltip>
                            </Box>
                        }
                        sx={{ ml: 1, mr: 0 }}
                    />
                )}
                {setSpatialCloseness !== undefined && (
                    <FormControlLabel
                        control={
                            <Switch
                                checked={spatialCloseness}
                                onChange={(e) => setSpatialCloseness(e.target.checked)}
                                color="primary"
                                size="small"
                            />
                        }
                        label={
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.3}}>
                                <Typography variant="caption">
                                    {t('spatial_closeness.label', 'Spatial Closeness')}
                                </Typography>
                                <Tooltip
                                    title={t('spatial_closeness.tooltip', 'When enabled, the AI will look for related information in the same area of the document')}
                                    arrow
                                >
                                    <InfoOutlinedIcon sx={{fontSize: 14, color: 'text.secondary'}} />
                                </Tooltip>
                            </Box>
                        }
                        sx={{ ml: 1, mr: 0 }}
                    />
                )}

                {setUseSpatialLocality !== undefined && (
                    <FormControlLabel
                        control={
                            <Switch
                                checked={useSpatialLocality}
                                onChange={(e) => setUseSpatialLocality(e.target.checked)}
                                color="primary"
                                size="small"
                            />
                        }
                        label={
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.3}}>
                                <Typography variant="caption">
                                    {t('spatial_locality.label', 'Spatial Locality')}
                                </Typography>
                                <Tooltip
                                    title={t('spatial_locality.tooltip', 'When enabled, only searches document regions where examples were found. Recommended for similar layouts (invoices, receipts). Disable for varying layouts (contracts, reports).')}
                                    arrow
                                >
                                    <InfoOutlinedIcon sx={{fontSize: 14, color: 'text.secondary'}} />
                                </Tooltip>
                            </Box>
                        }
                        sx={{ ml: 1, mr: 0 }}
                    />
                )}
                </Box>
            </Box>
        </Container>
    );
}

export default Header;
